import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90',
      },
      density: {
        default: '',
        compact: [
          'px-3 py-2 text-xs',
          'has-[>svg]:grid-cols-[calc(var(--spacing)*3)_1fr] [&>svg]:size-3',
          'gap-y-0 has-[>svg]:gap-x-2',
          '[&>svg]:translate-y-0',
          '[&_[data-slot=alert-title]]:text-[13px] [&_[data-slot=alert-title]]:min-h-0',
          '[&_[data-slot=alert-description]]:text-xs [&_[data-slot=alert-description]]:gap-0.5',
          '[&_[data-slot=alert-description]_*]:leading-snug',
          '[&_[data-slot=alert-description]_ul]:pl-4 [&_[data-slot=alert-description]_ol]:pl-4',
        ].join(' '),
        tight: [
          'px-2.5 py-1.5 text-[13px]',
          'has-[>svg]:grid-cols-[calc(var(--spacing)*2.5)_1fr] [&>svg]:size-3 [&>svg]:translate-y-0',
          'gap-y-0 has-[>svg]:gap-x-1.5',
          '[&_[data-slot=alert-title]]:text-[13px] [&_[data-slot=alert-title]]:min-h-0',
          '[&_[data-slot=alert-description]]:text-[13px] [&_[data-slot=alert-description]]:gap-0.5',
          '[&_[data-slot=alert-description]_*]:leading-tight',
          '[&_[data-slot=alert-description]_ul]:pl-4 [&_[data-slot=alert-description]_ol]:pl-4',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'default',
      density: 'default',
    },
  },
);

function Alert({
  className,
  variant,
  density,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant, density }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 grid justify-items-start gap-1 [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle };
